import React from 'react'

export default function BiometricRecordsTable({
  records = [],
  loading = false,
  sortBy = 'rawTime',
  sortOrder = 'asc',
  onSort,
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-xs text-on-surface">
          {/* ── Table Header ── */}
          <thead className="border-b border-outline-variant/25 bg-surface-container-low/80 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant select-none">
            <tr>
              {/* Time */}
              <th
                onClick={() => onSort('rawTime')}
                className="w-44 px-5 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Time</span>
                  {sortBy === 'rawTime' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* ID Card No */}
              <th
                onClick={() => onSort('idCardNo')}
                className="w-36 px-4 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>ID No. (Card)</span>
                  {sortBy === 'idCardNo' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Roll No */}
              <th
                onClick={() => onSort('rollNo')}
                className="w-24 px-4 py-3.5 text-center cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Roll No.</span>
                  {sortBy === 'rollNo' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Name & Batch */}
              <th
                onClick={() => onSort('fullName')}
                className="px-5 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Name / Batches</span>
                  {sortBy === 'fullName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Branch & Terminal */}
              <th
                onClick={() => onSort('branchName')}
                className="w-56 px-5 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Branch / Terminal</span>
                  {sortBy === 'branchName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>

          {/* ── Table Body ── */}
          <tbody className="divide-y divide-outline-variant/15 font-medium">
            {loading ? (
              <tr>
                <td colSpan={5} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                      progress_activity
                    </span>
                    <span>Loading machine swipe logs...</span>
                  </div>
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline-variant">
                      badge
                    </span>
                    <p className="font-bold text-on-surface">No Biometric Records Found</p>
                    <p className="text-[11px]">No swipe events recorded for the selected date and filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              records.map((record) => {
                const isIN = record.swipeType === 'IN'
                return (
                  <tr
                    key={record.id}
                    className="group transition-colors hover:bg-surface-container-low/50"
                  >
                    {/* Time */}
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center justify-center rounded-lg px-1.5 py-0.5 text-[10px] font-black ${
                            isIN
                              ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/30'
                              : 'bg-amber-500/10 text-amber-700 border border-amber-500/30'
                          }`}
                        >
                          {record.swipeType}
                        </span>
                        <span className="font-mono text-xs font-bold text-on-surface">
                          {record.sTime}
                        </span>
                      </div>
                    </td>

                    {/* ID Card No */}
                    <td className="px-4 py-3.5 align-middle">
                      <span className="inline-flex items-center gap-1 rounded-xl bg-surface-container-high px-2.5 py-1 text-xs font-mono font-bold text-on-surface">
                        <span className="material-symbols-outlined text-xs text-on-surface-variant">
                          contactless
                        </span>
                        <span>{record.idCardNo}</span>
                      </span>
                    </td>

                    {/* Roll No */}
                    <td className="px-4 py-3.5 align-middle text-center">
                      <span className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-2.5 py-1 text-xs font-black text-on-surface">
                        {record.rollNo}
                      </span>
                    </td>

                    {/* Name / Course / Batch */}
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-display text-sm font-extrabold text-[#3b82f6]">
                          {record.fullName}
                        </span>
                        <div className="flex items-center gap-1.5 text-[11px] text-on-surface-variant">
                          <span className="font-semibold text-primary">{record.courseName}</span>
                          <span>•</span>
                          <span>{record.batchNames}</span>
                        </div>
                      </div>
                    </td>

                    {/* Branch & Terminal */}
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface">
                          {record.branchName}
                        </span>
                        <span className="text-[10px] text-on-surface-variant font-mono">
                          {record.deviceId}
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
